import { z } from 'zod';
import { purchaseOrderQuantitySchema } from '../purchase-order/item-quantity.js';
import { isValidUuidV7, requiredUuidString } from '../zod-helpers/index.js';
import { MAX_RECEIVE_LINES, MAX_RECEIVE_LINES_MESSAGE, RECEIVE_INPUT_CONSTRAINTS, } from './constraints.js';
const receivePurchaseOrderItemsLineSchema = z.object({
    // Lower-cased at the boundary, not at each comparison. PostgreSQL renders `uuid` columns
    // lower-case, so a client sending upper-case ids would key the batch one way and the persisted
    // rows another — enough to make a workflow replay reject a valid receive as "already created
    // with different lines". Fixing it once here keeps every downstream key a true mirror.
    purchaseOrderItemId: requiredUuidString(`${RECEIVE_INPUT_CONSTRAINTS.purchaseOrderItem.label} is required.`, `Invalid ${RECEIVE_INPUT_CONSTRAINTS.purchaseOrderItem.label.toLowerCase()} selection.`).transform((value) => value.toLowerCase()),
    quantity: purchaseOrderQuantitySchema,
    locationId: requiredUuidString(`${RECEIVE_INPUT_CONSTRAINTS.location.label} is required.`, `Invalid ${RECEIVE_INPUT_CONSTRAINTS.location.label.toLowerCase()} selection.`).transform((value) => value.toLowerCase()),
    lotNumber: z
        .string()
        .trim()
        .max(RECEIVE_INPUT_CONSTRAINTS.lotNumber.maxLength, `${RECEIVE_INPUT_CONSTRAINTS.lotNumber.label} must be ${RECEIVE_INPUT_CONSTRAINTS.lotNumber.maxLength} characters or fewer.`)
        .nullish()
        .transform((value) => (value == null || value === '' ? undefined : value)),
});
export const receivePurchaseOrderItemsInputSchema = z.object({
    // purchaseOrderId matters most of the four: it is concatenated into the Temporal workflow id
    // (`purchase-order-receive-<id>`), which is compared byte-exactly, unlike a Postgres `uuid`
    // predicate. Two callers sending the same purchase order in different case would start two
    // concurrent workflows with two receipt ids, sliding straight past the already-running fence
    // and posting the receive twice.
    purchaseOrderId: requiredUuidString(`${RECEIVE_INPUT_CONSTRAINTS.purchaseOrder.label} is required.`, `Invalid ${RECEIVE_INPUT_CONSTRAINTS.purchaseOrder.label.toLowerCase()} selection.`).transform((value) => value.toLowerCase()),
    locationGroupId: requiredUuidString(`${RECEIVE_INPUT_CONSTRAINTS.locationGroup.label} is required.`, `Invalid ${RECEIVE_INPUT_CONSTRAINTS.locationGroup.label.toLowerCase()} selection.`).transform((value) => value.toLowerCase()),
    lines: z
        .array(receivePurchaseOrderItemsLineSchema)
        .min(1, 'At least one line to receive is required.')
        .max(MAX_RECEIVE_LINES, MAX_RECEIVE_LINES_MESSAGE)
        .superRefine((lines, context) => {
        if (lines.length > MAX_RECEIVE_LINES)
            return;
        const seen = new Set();
        const duplicated = new Set();
        const lotByItem = new Map();
        for (const [index, line] of lines.entries()) {
            if (!line.purchaseOrderItemId ||
                !line.locationId ||
                !isValidUuidV7(line.purchaseOrderItemId) ||
                !isValidUuidV7(line.locationId)) {
                continue;
            }
            // Already lower-cased by the field transforms above, so these are the same strings the
            // database and every downstream key-builder will see.
            const purchaseOrderItemIdKey = line.purchaseOrderItemId;
            const locationIdKey = line.locationId;
            const pairKey = `${purchaseOrderItemIdKey}:${locationIdKey}`;
            if (seen.has(pairKey)) {
                context.addIssue({
                    code: 'custom',
                    path: [index, 'purchaseOrderItemId'],
                    message: 'Duplicate purchase order item and location in receive batch.',
                });
                duplicated.add(index);
            }
            else {
                seen.add(pairKey);
            }
            if (!duplicated.has(index)) {
                if (lotByItem.has(purchaseOrderItemIdKey)) {
                    const previous = lotByItem.get(purchaseOrderItemIdKey);
                    if (previous !== line.lotNumber) {
                        context.addIssue({
                            code: 'custom',
                            path: [index, 'lotNumber'],
                            message: 'Split receives of the same line must use the same lot number.',
                        });
                    }
                }
                else {
                    lotByItem.set(purchaseOrderItemIdKey, line.lotNumber);
                }
            }
        }
    }),
    /** The resolver gates allowOverReceipt: true on receiving:allowOverReceipt, a separate elevated permission. */
    allowOverReceipt: z
        .boolean()
        .nullish()
        .transform((value) => value ?? false),
    /**
     * Acknowledges that one or more lines will merge into a lot tag already holding stock in
     * the target bin. The resolver refuses an unconfirmed receive in that case so the operator
     * sees what is about to be combined; it carries no permission of its own.
     */
    confirmLotMerge: z
        .boolean()
        .nullish()
        .transform((value) => value ?? false),
});
//# sourceMappingURL=receive-input.js.map